import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { accountsApi } from '@/services/accountsService';
import { authApi } from '@/services/authService';
import { dashboardApi } from '@/services/dashboadService';
import { documentCenterApi } from '@/services/documentCenterService';
import { downloadCenterApi } from '@/services/downloadCenterService';
import { ipoApi } from '@/services/ipoService';
import { ipoBetaApi } from '@/services/ipoBetaService';
import { kycApi } from '@/services/kycCardService';
import { mandateApi } from '@/services/mandateService';
import { modificationApi, panDetailsApi } from '@/services/modificationService';
import { mtfApi } from '@/services/mtfService';
import { notificationsApi } from '@/services/notificationsService';
import { profileApi } from '@/services/profileService';
import { liveRiskApi } from '@/services/liveRiskService';
import { reportsApi } from '@/services/reportsService';
import { ePledgeApi } from '@/services/ePledgeService';
// import { stockVerificationApi } from '@/services/stockVerificationService';
import { passwordApi } from '@/services/passwordService';
import { wealthApi } from '@/services/wealth';
import { mutualFundApi } from '@/services/mutualFund';
import { pmsApi } from '@/services/pmsAPI';

import { RESET_ALL } from './actions/resetActions';
import financialYearSlice from './slices/financialYearSlice';
import uiSlice from './slices/uiSlice';
import userSlice from './slices/userSlice';
import { marketInsightApi } from '@/services/marketInsight';
import { newsApi } from '@/services/newsService';

// ================================================================
// ROOT REDUCER & META-REDUCER (GLOBAL RESET)
// ================================================================
//
// Purpose: Provide a single RESET_ALL action that purges entire Redux state.
// Implementation: Wrap combined reducers in meta-reducer that returns
// undefined state on RESET_ALL so each slice reinitializes to its initial state.
//
// Safety: Does NOT alter reducer logic or middleware; only intercepts one
// explicit action type. RTK Query caches also reset because their reducers
// receive undefined root state and rehydrate to initial state.
// ================================================================

const appReducer = combineReducers({
  user: userSlice,
  ui: uiSlice,
  financialYear: financialYearSlice,
  [authApi.reducerPath]: authApi.reducer,
  [kycApi.reducerPath]: kycApi.reducer,
  [profileApi.reducerPath]: profileApi.reducer,
  [accountsApi.reducerPath]: accountsApi.reducer,
  [ipoApi.reducerPath]: ipoApi.reducer,
  [ipoBetaApi.reducerPath]: ipoBetaApi.reducer,
  [mandateApi.reducerPath]: mandateApi.reducer,
  [modificationApi.reducerPath]: modificationApi.reducer,
  [panDetailsApi.reducerPath]: panDetailsApi.reducer,
  [liveRiskApi.reducerPath]: liveRiskApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [downloadCenterApi.reducerPath]: downloadCenterApi.reducer,
  [documentCenterApi.reducerPath]: documentCenterApi.reducer,
  [mtfApi.reducerPath]: mtfApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [wealthApi.reducerPath]: wealthApi.reducer,
  [mutualFundApi.reducerPath]: mutualFundApi.reducer,
  [marketInsightApi.reducerPath]: marketInsightApi.reducer,
  [newsApi.reducerPath]: newsApi.reducer,
  // [stockVerificationApi.reducerPath]: stockVerificationApi.reducer,
  [ePledgeApi.reducerPath]: ePledgeApi.reducer,
  [pmsApi.reducerPath]: pmsApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [passwordApi.reducerPath]: passwordApi.reducer,
});

const rootReducer: typeof appReducer = (state, action) => {
  if (action.type === RESET_ALL) {
    state = undefined; // Trigger full state re-initialization
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(
      authApi.middleware,
      kycApi.middleware,
      profileApi.middleware,
      liveRiskApi.middleware,
      accountsApi.middleware,
      ipoApi.middleware,
      ipoBetaApi.middleware,
      mandateApi.middleware,
      modificationApi.middleware,
      panDetailsApi.middleware,
      reportsApi.middleware,
      downloadCenterApi.middleware,
      documentCenterApi.middleware,
      mtfApi.middleware,
      dashboardApi.middleware,
      wealthApi.middleware,
      mutualFundApi.middleware,
      marketInsightApi.middleware,
      newsApi.middleware,
      // stockVerificationApi.middleware,
      ePledgeApi.middleware,
      pmsApi.middleware,
      notificationsApi.middleware,
      passwordApi.middleware,
    ),
  devTools: import.meta.env.NODE_ENV !== 'production',
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
